import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'

import { updateGroup } from '@/api/groups-api'
import type { GroupDetail } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { GroupActionDialog } from './group-action-dialog'
import { groupKeys } from './group-keys'

/**
 * The step nobody could take: a group is created Forming and only an Active group can borrow,
 * but no screen ever offered the transition — the tenth control this system has shipped correct
 * and unsatisfiable, found by a reviewer who could not finish opening a loan account.
 *
 * The domain's own rules apply unchanged: activation demands a leader, and the reason travels
 * with the status the way every status change here does.
 */
export function ActivateGroupAction({ group }: { group: GroupDetail }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      updateGroup(group.id, {
        name: group.name,
        formationDate: group.formationDate,
        status: 'Active',
        statusReason: reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.all })
      setOpen(false)
    },
  })

  if (group.status !== 'Forming') {
    return null
  }

  const hasLeader = group.leaderCustomerId !== null

  return (
    <>
      <Button
        variant="primary"
        onClick={() => setOpen(true)}
        disabled={!hasLeader}
        title={
          hasLeader
            ? undefined
            : 'Appoint a leader first — an Active group must have one.'
        }
      >
        <CheckCircle2 />
        Activate the group
      </Button>

      <GroupActionDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) {
            setReason('')
            mutation.reset()
          }
        }}
        title={`Activate ${group.name}`}
        description="An Active group can be scheduled, attend meetings, and take loan rounds. Forming is for admitting members; this is the step that ends it."
        confirmLabel="Activate"
        isPending={mutation.isPending}
        disabled={reason.trim().length === 0}
        error={mutation.error ?? null}
        onConfirm={() => mutation.mutate()}
      >
        <div className="space-y-1.5">
          <Label htmlFor="activate-reason">Reason</Label>
          <Input
            id="activate-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why the group is ready"
          />
          <p className="text-xs text-muted">
            Recorded with the status change — e.g. membership complete, first cycle ready.
          </p>
        </div>
      </GroupActionDialog>
    </>
  )
}
